import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import api from "../lib/api";
import { Loader2, Clock, Search, ArrowRight } from "lucide-react";
import dayjs from "dayjs";
import { useSeo } from "../lib/seo";

const CAT_STYLE = {
  "Travel Guides": "bg-[#e8f6ff] text-[#0369a1] border-[#bae6fd]",
  "Rental Tips": "bg-[#f0f9f0] text-[#009900] border-[#bbe5bb]",
  "Company News": "bg-[#fef9e7] text-[#a16207] border-[#fde68a]",
  "Sustainability": "bg-[#ecfdf5] text-[#047857] border-[#a7f3d0]",
};

export default function Blog() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [category, setCategory] = useState("All");
  const [query, setQuery] = useState("");
  useSeo({
    title: "Stories From The Road — UAE Travel Guides & Rental Tips | Europcar UAE",
    description: "Travel guides, honest rental tips, company news and sustainability stories from Europcar Dubai & Northern Emirates.",
  });

  useEffect(() => {
    api.get("/blog").then((r) => setData(r.data)).catch(() => setData({ categories: [], posts: [] }));
  }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    return data.posts.filter(
      (p) =>
        (category === "All" || p.category === category) &&
        (p.title + p.excerpt).toLowerCase().includes(query.toLowerCase())
    );
  }, [data, category, query]);

  const featured = filtered[0];
  const rest = filtered.slice(1);

  return (
    <div className="min-h-screen flex flex-col bg-[#F8FAFC]">
      <Header />

      <section className="bg-[#144733] relative overflow-hidden">
        <div className="absolute -right-24 -top-24 w-96 h-96 rounded-full bg-[#009900]/30 blur-3xl" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20 relative">
          <p className="text-[#FFDC00] text-xs uppercase tracking-[0.25em] font-semibold mb-3">{t("The Europcar UAE Journal")}</p>
          <h1 className="heading-upper text-3xl sm:text-4xl lg:text-5xl text-white max-w-2xl leading-tight" data-testid="blog-heading">
            {t("Stories from the road.")}
          </h1>
          <p className="text-white/75 text-base mt-4 max-w-xl">
            {t("Travel guides, honest rental tips, company news and our sustainability journey — written the way we'd tell a friend.")}
          </p>
        </div>
      </section>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-12 w-full flex-1" data-testid="blog-page">
        <div className="flex flex-wrap items-center gap-3 mb-10">
          {["All", ...(data?.categories || [])].map((c) => (
            <button key={c} onClick={() => setCategory(c)}
              className={`px-4 py-2 rounded-full text-xs font-medium uppercase tracking-wide transition-colors border ${category === c ? "bg-[#009900] text-white border-[#009900]" : c === "All" ? "bg-white border-[#E2E8F0] text-[#144733] hover:border-[#009900]" : `${CAT_STYLE[c] || "bg-white border-[#E2E8F0] text-[#144733]"} hover:border-[#009900]`}`}
              data-testid={`blog-category-pill-${c.toLowerCase().replace(/\s+/g, "-")}`}>
              {c === "All" ? t("All") : t(c)}
            </button>
          ))}
          <div className="relative ml-auto w-full sm:w-64">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8a978f]" />
            <input className="field-input pl-9" placeholder={t("Search articles…")} value={query}
              onChange={(e) => setQuery(e.target.value)} data-testid="blog-search-input" />
          </div>
        </div>

        {!data ? (
          <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-[#009900]" /></div>
        ) : filtered.length === 0 ? (
          <p className="text-center text-sm text-[#57685e] py-20" data-testid="blog-no-results">{t("No articles match your search.")}</p>
        ) : (
          <>
            {featured && (
              <Link to={`/blog/${featured.slug}`}
                className="group grid lg:grid-cols-2 bg-white rounded-2xl border border-[#E2E8F0] overflow-hidden mb-12 card-lift"
                data-testid="featured-article">
                <div className="h-64 lg:h-auto overflow-hidden">
                  <img src={featured.image} alt={featured.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-8 lg:p-12 flex flex-col justify-center">
                  <span className="text-[10px] uppercase tracking-widest font-semibold text-[#009900] mb-3">{t(featured.category)}</span>
                  <h2 className="text-2xl lg:text-3xl font-bold text-[#0F1E17] leading-snug group-hover:text-[#005125] transition-colors">{featured.title}</h2>
                  <p className="text-sm text-[#57685e] mt-4 leading-relaxed">{featured.excerpt}</p>
                  <div className="flex items-center gap-4 mt-6 text-xs text-[#8a978f]">
                    <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {featured.reading_time} {t("min read")}</span>
                    <span>{dayjs(featured.published).format("DD MMM YYYY")}</span>
                    <span className="ml-auto text-[#009900] font-semibold uppercase tracking-wide flex items-center gap-1">{t("Read")} <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" /></span>
                  </div>
                </div>
              </Link>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {rest.map((p) => (
                <Link key={p.slug} to={`/blog/${p.slug}`}
                  className="group bg-white rounded-2xl border border-[#E2E8F0] overflow-hidden card-lift flex flex-col"
                  data-testid={`blog-card-${p.slug}`}>
                  <div className="h-44 overflow-hidden">
                    <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <span className="text-[10px] uppercase tracking-widest font-semibold text-[#009900] mb-2">{t(p.category)}</span>
                    <h3 className="font-bold text-[#0F1E17] leading-snug group-hover:text-[#005125] transition-colors">{p.title}</h3>
                    <p className="text-xs text-[#57685e] mt-2 leading-relaxed line-clamp-3">{p.excerpt}</p>
                    <div className="flex items-center gap-3 mt-auto pt-4 text-[11px] text-[#8a978f]">
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {p.reading_time} {t("min")}</span>
                      <span>{dayjs(p.published).format("DD MMM YYYY")}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
